import classNames from 'classnames';
import { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react';
import { Trans } from 'react-i18next';

import { Alert, AutoshipmentLogoLockup, Checkbox, InfoTag } from '@zooplus/ui-components';

import CardNumberFormField from '@components/CardNumberFormField/CardNumberFormField';
import CVVIcon from '@components/CVVIcon/CVVIcon';
import DetectedCardBrandsController from '@components/DetectedCardBrandsController/DetectedCardBrandsController';
import FormControl from '@components/FormControl/FormControl';
import FormControlWrapper from '@components/FormControlWrapper/FormControlWrapper';
import SupportedCardList from '@components/SupportedCardList/SupportedCardList';
import useAdyenSecureFields from '@hooks/useAdyenSecureFields';
import useAdyenState from '@hooks/useAdyenState';
import { AdyenCreditCardFormData } from '@hooks/useAdyenState.static';
import useIsMobile from '@hooks/useIsMobile';
import { useTranslation } from '@hooks/useTranslations';
import { useAppContext } from '@providers/AppContextProvider';
import { CreditCardBrand, Nullable, Sizes } from '@types-internal';
import { AdyenConfig, adyenBrandOfCreditCardBrand } from '@types-internal/AdyenTypes';
import { exposeGlobally } from '@utils/utils';

import styles from './AdyenCreditCardForm.module.scss';
import { CREDIT_CARD_RENDER_CONTAINER_ID } from './AdyenCreditCardForm.static';

import '@adyen/adyen-web/dist/adyen.css';

export type AdyenCreditCardFormRef = {
  computeAdyenCreditCardFormData: () => Nullable<AdyenCreditCardFormData>;
};

export interface AdyenCreditCardFormProps extends AdyenConfig {
  supportedCreditCardBrands: CreditCardBrand[];
  allowVaulting: boolean;
  errorMessage?: string;
  labelInline: boolean;
  isRoutedToAdyenDueToMarketplace?: boolean;
  customerHasCreditCardAutoshipment?: boolean;
  isCartWithMarketplaceItems?: boolean;
  onFatalError: (error: unknown, hideForm?: boolean) => void;
}

/**
 * The UI to add a new credit card, the security is handled by Adyen.
 * Relies on the hook 'useAdyenState' (to capture the data from Adyen and the unmanaged inputs) and 'useAdyenSecureFields' (to initialize the Adyen 'securedfields').
 *
 * Captured data can be retrieved with the globally-exposed 'computeAdyenCreditCardFormData'.
 */
const AdyenCreditCardForm = forwardRef<AdyenCreditCardFormRef, AdyenCreditCardFormProps>(
  (
    {
      adyenEnvironment,
      supportedCreditCardBrands,
      clientKey,
      errorMessage,
      allowVaulting,
      labelInline,
      isRoutedToAdyenDueToMarketplace,
      customerHasCreditCardAutoshipment,
      isCartWithMarketplaceItems,
      onFatalError,
    },
    forwardedRef,
  ) => {
    const { t } = useTranslation();
    const { locale } = useAppContext();
    const isMobile = useIsMobile(Sizes.MD);
    const {
      detectedCardBrands,
      onChange,
      onFieldValid,
      onHolderNameChange,
      storeInVault,
      onStoreInVaultChange,
      focusedElement,
      onFocus,
      creditCardBrand,
      onBrand,
      onBinLookup,
      computeAdyenCreditCardFormData,
      errors,
    } = useAdyenState(onFatalError);

    const secureFields = useAdyenSecureFields(
      {
        clientKey,
        locale,
        t,
        containerId: CREDIT_CARD_RENDER_CONTAINER_ID,
        adyenEnvironment,
        onChange,
        onFocus,
        onFieldValid,
        onBrand,
        onBinLookup,
        onFatalError,
      },
      supportedCreditCardBrands,
    );

    // Expose the captured state globally so that external code can get the Adyen encrypted data
    useEffect(
      () => exposeGlobally('computeAdyenCreditCardFormData', computeAdyenCreditCardFormData),
      [computeAdyenCreditCardFormData],
    );

    // Expose the computeAdyenCreditCardFormData method internally too
    useImperativeHandle(forwardedRef, () => ({
      computeAdyenCreditCardFormData,
    }));

    /* Handle the card brand selection in case of co-branded */
    const onDualBrandingSelected = useCallback(
      (brand: CreditCardBrand) => {
        const adyenBrand = adyenBrandOfCreditCardBrand[brand];
        if (secureFields && adyenBrand) {
          secureFields.dualBrandingChangeHandler(adyenBrand);
        }
      },
      [secureFields],
    );

    const isAmex = creditCardBrand === 'american_express';
    const noCardBrandDetected = !creditCardBrand;
    const cardBrandDetected = !!creditCardBrand;

    const detectedBrandController = (
      <DetectedCardBrandsController
        detectedCardBrands={detectedCardBrands}
        selectedBrand={creditCardBrand}
        onBrandSelected={onDualBrandingSelected}
      />
    );

    return (
      <div id={CREDIT_CARD_RENDER_CONTAINER_ID} data-zta="adyen-cc-form-container">
        {errorMessage && (
          <Alert
            className={styles.errorMessage}
            variant="warning"
            data-zta="adyen-credit-card-form-alert">
            {errorMessage}
          </Alert>
        )}
        <div className={styles.supportedCardsBar}>
          {noCardBrandDetected && (
            // When no card brand detected, list of all supported brands should be displayed above the form
            <SupportedCardList cardBrands={supportedCreditCardBrands} />
          )}
          {cardBrandDetected && isMobile && (
            // When card brand is detected, and we are on mobile screen size, the detected card brands controller
            // should be displayed above the form, outside the card number input field, opposite with if we are
            // on larger screen size (desktop), the detected card brands controller is displayed inside the card
            // number input field.
            <>{detectedBrandController}</>
          )}
        </div>
        <CardNumberFormField
          // Render the detected brand controller inside the input field only when we are on large screen (not on
          // mobile or smaller screen size).
          id="encryptedCardNumber"
          detectedBrandsController={isMobile ? undefined : detectedBrandController}
          errorMessage={errors.encryptedCardNumber}
          focused={focusedElement === 'encryptedCardNumber'}
          labelInline={labelInline}
        />
        <FormControlWrapper
          label={t('checkout.payment.creditcard.holder_name')}
          labelFor="holderName"
          labelInline={labelInline}
          errorMessage={errors.holderName}
          errorZta="errorInputCardName">
          <input
            onChange={onHolderNameChange}
            className={classNames(
              'z-text-input',
              'z-input',
              styles.holderName,
              errors.holderName !== null && 'z-text-input--error',
            )}
            type="text"
            name="cc-name"
            autoComplete="cc-name"
            id="holderName"
            data-zta="inputCardName"
            aria-invalid={errors.holderName !== null}
          />
        </FormControlWrapper>

        <FormControlWrapper
          label={t('checkout.payment.creditcard.expiry_date')}
          labelFor="encryptedExpiryDate"
          labelInline={labelInline}
          errorMessage={errors.encryptedExpiryDate}
          errorZta="errorInputCardDate">
          <div
            id="encryptedExpiryDate"
            className={classNames(styles.notFullWidth, styles.expiryDateContainer)}>
            <FormControl
              className={styles.fullWidth}
              innerClassname={classNames(styles.noPadding)}
              id="encryptedExpiryMonth"
              data-cse="encryptedExpiryMonth"
              hasError={errors.encryptedExpiryDate !== null}
              focused={focusedElement === 'encryptedExpiryMonth'}
              data-zta="inputCardMonth"
            />
            <span>/</span>
            <FormControl
              className={styles.fullWidth}
              innerClassname={classNames(styles.noPadding)}
              id="encryptedExpiryYear"
              data-cse="encryptedExpiryYear"
              hasError={errors.encryptedExpiryDate !== null}
              focused={focusedElement === 'encryptedExpiryYear'}
              data-zta="inputCardYear"
            />
          </div>
        </FormControlWrapper>

        <FormControlWrapper
          label={t('checkout.payment.creditcard.cvc.generic_code')}
          labelFor="encryptedSecurityCode"
          labelInline={labelInline}
          infoMessage={
            isAmex
              ? t('checkout.payment.creditcard.cid.warning')
              : t('checkout.payment.creditcard.cvc.warning')
          }
          errorMessage={errors.encryptedSecurityCode}
          errorZta="errorInputCardCode">
          <FormControl
            innerClassname={classNames(styles.withIconWithin, styles.noPadding)}
            notFullWidth
            id="encryptedSecurityCode"
            data-cse="encryptedSecurityCode"
            appendixItems={<CVVIcon isAmex={isAmex} />}
            hasError={errors.encryptedSecurityCode !== null}
            focused={focusedElement === 'encryptedSecurityCode'}
            data-zta="inputCardCode"></FormControl>
        </FormControlWrapper>

        {allowVaulting && (
          <FormControlWrapper labelInline={labelInline}>
            <Checkbox
              className={styles.vaultCheck}
              id="vault-check"
              checked={storeInVault}
              onChange={onStoreInVaultChange}
              data-zta="storeCreditCard">
              <div>
                {isRoutedToAdyenDueToMarketplace &&
                customerHasCreditCardAutoshipment &&
                isCartWithMarketplaceItems ? (
                  <Trans
                    t={t}
                    i18nKey="checkout.payment.save.creditcard.autoshipment"
                    components={{
                      ZooplusRepeat: (
                        <AutoshipmentLogoLockup
                          data-zta="autoshipmentLogo"
                          aria-label="Autoshipment Logo"
                          className={styles.autoshipmentLogo}
                          locale={locale}
                        />
                      ),
                    }}
                  />
                ) : (
                  t('checkout.payment.save.creditcard')
                )}

                <br />
                {isRoutedToAdyenDueToMarketplace && (
                  <InfoTag variant="danger">{t('general.recommended')}</InfoTag>
                )}
              </div>
            </Checkbox>
          </FormControlWrapper>
        )}
      </div>
    );
  },
);

export default AdyenCreditCardForm;
